/**
 * The shared roster-card resolver — ONE answer to "what does this member's
 * card say", consumed by every host and every style.
 *
 * ── WHAT THIS IS AND IS NOT ──
 *
 * It resolves DATA, not markup. `PRODUCT_PLAN.md` §2.4 is explicit that each
 * POV keeps its own visual identity — the esports desaturated-staff
 * treatment, RTTF's fully composed creative, Monster's studio photography —
 * and that "sharing the *resolver* does not mean flattening the
 * *presentation*". So this returns a resolved model and each host renders it
 * in its own markup: PAGE's `CreatorRosterCard` row, HUB's rail card. A
 * visitor should see no difference from today.
 *
 * What it removes is the duplicated DECIDING. PAGE and HUB each worked out,
 * separately, what a member's name is, which image wins, whether they count
 * as live and what the line under the name says — and they did not agree.
 * That is the drift this ends.
 *
 * ── WHY IT EXTENDS RATHER THAN REPLACES ──
 *
 * `rosterMember.tsx`'s `cardUrl` / `channelUrl` / `memberKey` /
 * `highlightForMember` are correct today and are imported, not rewritten
 * (`PRODUCT_PLAN.md` §8). The identity, link-target and clip-matching rules
 * they encode are load-bearing and have their own hard-won reasons — see the
 * notes on each. This file adds the layer above them.
 *
 * ── THE RULE ──
 *
 * A POV's extra fields are config (`rosterFields.ts`), never a code branch.
 * There is no `pov` parameter on this function and no switch on one, which is
 * what makes adding a POV cost a preset entry rather than a renderer change.
 */

import {type RosterHighlight, type RosterFieldSpec} from "@devour/client";
import {type RosterMember, cardUrl, initials, memberKey, highlightForMember} from "./rosterMember";
import {resolveRosterFields, type ResolvedRosterField} from "@/surfaces/rosterFields";
import {SlotFootprint} from "@/surfaces/slots";

/**
 * What is true about this member right now, decided ONCE.
 *
 * Both hosts previously derived this inline from `isLive` / `offGame` /
 * `effectiveLiveState` / clip presence, and reached different conclusions —
 * PAGE distinguished a clip state, HUB did not; HUB badged off-title under
 * every filter mode, PAGE did not. Collapsing it to a single enum means a
 * host chooses how to PRESENT each state, never what the state is.
 */
export enum RosterLiveState {
    /** Live, and on a title this roster is about. The only state that plays in place. */
    LIVE_ON_TITLE = "LIVE_ON_TITLE",
    /** Live, on something else. Shown, labelled, and carrying no live data. */
    LIVE_OFF_TITLE = "LIVE_OFF_TITLE",
    /** Not live, but there is a clip of this title to watch. */
    CLIP = "CLIP",
    /** Confirmed dark. */
    OFFLINE = "OFFLINE",
    /**
     * No fresh observation. Distinct from OFFLINE on purpose: a stalled
     * detector otherwise prints a confident "Last live · X" for a whole
     * roster, claiming knowledge the surface does not have. Public hosts may
     * choose to render this exactly like OFFLINE — "Not checked" on a
     * publisher's own site reads as our tooling being broken — but they make
     * that choice knowingly rather than by losing the distinction here.
     */
    UNKNOWN = "UNKNOWN",
}

/** One member's card, resolved. Every host renders this and nothing else. */
export interface ResolvedRosterCard {
    /** Stable identity — `memberKey`, never `dropSlug`. See that function. */
    key: string;
    name: string;
    /** The winning image, or undefined when the member has none at all. */
    imageUrl?: string;
    /** Fallback when there is no image. Never derived per host. */
    initials: string;
    liveState: RosterLiveState;
    /** The line under the name: stream title, clip title, or last-played. */
    detail?: string;
    /** Where the card goes when it is not playable. Undefined ⇒ goes nowhere. */
    href?: string;
    /** The other title, when LIVE_OFF_TITLE and we know it. */
    offTitleGame?: string;
    /** The clip this member has of this title, when one matched. */
    clip?: RosterHighlight;
    /** POV-declared extras, already filtered to those that resolved. */
    fields: ResolvedRosterField[];
    /** True when this member has a channel at all. */
    hasChannel: boolean;
}

/**
 * A roster card's footprint, declared for BOTH structures.
 *
 * `anchor-driven-surfaces` Rule 5: a widget declares a footprint in the PAGE
 * and HUB structures alike, and never gets a third rendering path. A roster
 * is a horizontal rail on HUB (the games/rewards/roster spine position) and a
 * full-width band on PAGE, where nothing floats.
 */
export const ROSTER_CARD_FOOTPRINT = {
    HUB: SlotFootprint.HORIZONTAL_RAIL,
    PAGE: SlotFootprint.BAND,
} as const;

export interface ResolveRosterCardOptions {
    /**
     * The roster's declared extra fields, off the payload
     * (`DropRosterResponse.rosterFields`) — the team's authored schema with
     * the surface's additions merged over it.
     *
     * NOT from a POV preset. These were POV constants until F.4; a roster's
     * fields belong to whoever runs the roster, so any roster on any POV can
     * declare any of them.
     */
    fieldSpecs?: readonly RosterFieldSpec[];
    /** Values for those fields, by key. Absent providers simply yield nothing. */
    fieldValues?: Readonly<Record<string, unknown>>;
    /** This roster's clips, so the clip state can be resolved here rather than per host. */
    highlights?: readonly RosterHighlight[];
    /**
     * Treat an off-title member as plain — no off-title state, no other-title
     * name. Set under `LiveFilterMode.STRICT`, where the roster's operator has
     * switched off-title presence off. See `suppressesOffTitle` in
     * `dropSpineData.ts`, which is the one place that decision is made.
     */
    suppressOffTitle?: boolean;
    /**
     * What the operator declared counts as on-title, straight off the roster
     * payload (`DropRosterResponse.filterGameNames`).
     *
     * ── WHY THE RESOLVER NEEDS THIS AND CANNOT USE `offGame` ALONE ──
     *
     * The endpoint computes `offGame` only for members who are LIVE
     * (`applyRosterLiveFilter.ts` returns `offGame: false` on its `!isLive`
     * early return). For an OFFLINE member it is always false, while
     * `gameName` stays frozen at whatever they last streamed — the model
     * documents that field as "Frozen on stream.offline".
     *
     * So a card built from `offGame` alone prints another title's name,
     * unqualified and unbadged, for every offline member whose last session
     * was elsewhere. On a Call of Duty roster that reads `Last live · Marvel
     * Rivals` with nothing marking it as off-title — the exact conflation this
     * codebase already fixed once for the LIVE case.
     *
     * Omitted ⇒ no off-title derivation, which is the correct behaviour for a
     * host that has no filter list to judge against.
     */
    filterGameNames?: readonly string[];
}

/**
 * Is this member on a title other than the ones the roster is about?
 *
 * `offGame` covers the live case; the `gameName` comparison covers the offline
 * one. Matching is a containment test both ways because the two names are not
 * the same string — a roster says "Call of Duty: Modern Warfare 4" while the
 * filter says "Modern Warfare 4".
 */
export function isOffTitleFor(
    m: Pick<RosterMember, "offGame" | "gameName">,
    filterGameNames: readonly string[],
): boolean {
    if (m.offGame) return true;
    if (!m.gameName || filterGameNames.length === 0) return false;
    const game = m.gameName.toLowerCase();
    return !filterGameNames.some(name => {
        const f = name.toLowerCase();
        return game.includes(f) || f.includes(game);
    });
}

/**
 * Resolve one member into the card every host renders.
 *
 * Deliberately takes no `pov`. A POV reaches this function only as DATA, via
 * `fieldSpecs` — which is the whole mechanism that keeps a fifth POV from
 * costing a renderer change.
 */
export function resolveRosterCard(
    m: RosterMember,
    options: ResolveRosterCardOptions = {},
): ResolvedRosterCard {
    const {
        fieldSpecs = [], fieldValues, highlights = [], suppressOffTitle = false, filterGameNames = [],
    } = options;

    const clip = highlightForMember(m, highlights);
    /*
     * Off-title is decided HERE, for live and offline alike, and suppressed
     * BEFORE the state is chosen rather than after. A host receiving
     * LIVE_OFF_TITLE and being told to hide it would have to undo a decision
     * this function already made, which is how two hosts end up disagreeing
     * about the same member again.
     */
    const rawOffTitle = isOffTitleFor(m, filterGameNames);
    const offTitle = rawOffTitle && !suppressOffTitle;

    const liveState = m.isLive
        ? offTitle ? RosterLiveState.LIVE_OFF_TITLE : RosterLiveState.LIVE_ON_TITLE
        : clip
            ? RosterLiveState.CLIP
            : m.effectiveLiveState === "UNKNOWN"
                ? RosterLiveState.UNKNOWN
                : RosterLiveState.OFFLINE;

    /*
     * ── THE LINE UNDER THE NAME ──
     *
     * Live: what they are streaming. Clip: what the clip is. Otherwise what
     * they last played — except under UNKNOWN, where we have no fresh
     * observation and printing a last-played line asserts a fact we do not
     * have, and except when off-title is suppressed, where naming the other
     * title leaks exactly what the roster's operator switched off.
     */
    const detail = m.isLive
        ? m.streamTitle
        : liveState === RosterLiveState.CLIP
            ? clip?.title
            : liveState === RosterLiveState.UNKNOWN
                ? undefined
                /*
                 * An off-title member's last-played line names the OTHER title
                 * on this surface. It is withheld whenever the member is
                 * off-title at all — not only under STRICT — because printing
                 * a rival game's name unqualified on a publisher's roster is
                 * the claim, and the badge is what would have qualified it.
                 * `offTitleGame` still carries the name for a host that wants
                 * to render it deliberately, alongside the badge.
                 */
                : m.gameName && !rawOffTitle
                    ? `Last live · ${m.gameName}`
                    : undefined;

    return {
        key: memberKey(m),
        name: m.label ?? "",
        /*
         * `thumbnailUrl ?? profileImageUrl` — the order both hosts already
         * used. The backend has resolved the whole chain by this point,
         * including the operator's `overrideImageUrl`, which heads it; there
         * is deliberately no image logic here beyond preferring the richer of
         * the two the response carries.
         */
        imageUrl: m.thumbnailUrl ?? m.profileImageUrl,
        /* `label` is optional on the payload; `initials("")` yields the em-dash fallback. */
        initials: initials(m.label ?? ""),
        liveState,
        detail,
        href: cardUrl(m),
        /*
         * Carried for BOTH off-title states — live and offline. A host that
         * chooses to badge an offline off-title member needs the name to put
         * in the badge; withholding it here would leave that host re-deriving
         * the comparison this function already did.
         */
        offTitleGame: offTitle ? m.gameName : undefined,
        clip,
        fields: resolveRosterFields(fieldSpecs, fieldValues),
        hasChannel: Boolean(m.twitchChannelId || m.kickChannelSlug || m.youtubeChannelId),
    } satisfies ResolvedRosterCard;
}

/** Resolve a whole roster. Order is preserved — the payload's order is the operator's. */
export function resolveRosterCards(
    members: readonly RosterMember[],
    options: ResolveRosterCardOptions & {
        /** Per-member field values, keyed by `memberKey`. */
        fieldValuesByMember?: Readonly<Record<string, Record<string, unknown>>>;
    } = {},
): ResolvedRosterCard[] {
    const {fieldValuesByMember, ...shared} = options;
    return members.map(m => resolveRosterCard(m, {
        ...shared,
        fieldValues: fieldValuesByMember?.[memberKey(m)] ?? shared.fieldValues,
    }));
}
